import React, { useMemo } from 'react';
import { isEmpty } from 'lodash';
import { GatsbyImage } from "gatsby-plugin-image";
import styled, { css } from 'styled-components';
import { Link } from 'gatsby';

import { screen } from '@styles/mixins/breakpoints';

import Button from '@components/Button';
import ArticleContent from '@components/ArticleContent';
import InformationTitle from '@components/InformationTitle';

const getContainerStylesWithoutImages = (isImageSmall) => (isImageSmall
  ? css`
        display: flex;
      `
  : css`
        display: grid;
        padding: 0 16px 48px;
        grid-gap: 55px 32px;
        grid-template-columns: repeat(3, 1fr);
      `);

const getContainerStylesWithImage = () => css`
  display: flex;
  flex-direction: column;

  ${screen.l(css`
    padding-bottom: 0;
  `)}
`;

const StyledItemsContainer = styled.div`
  padding-bottom: ${({ isImageExist }) => (isImageExist ? '48px' : '48px')};
  margin: 0 auto;
  ${({ isImageSmall, isImageExist }) => (isImageExist
    ? getContainerStylesWithImage()
    : getContainerStylesWithoutImages(isImageSmall))}

  ${({ isImageExist }) => !isImageExist
    && screen.tablet(css`
      grid-template-columns: repeat(2, 1fr);
    `)}

  ${({ isImageExist }) => !isImageExist
    && screen.l(css`
      padding: 0 0 40px;
      grid-gap: initial;
      grid-template-columns: initial;
    `)}

  ${({ isImageSmall, isImageExist }) => isImageSmall
    && isImageExist
    && screen.l(css`
      padding: 0 0 40px;
    `)}

  ${({ cssRules }) => cssRules}
`;

const StyledItemContainer = styled.div`
  display: ${({ isImageExist }) => (isImageExist ? 'grid' : 'flex')};
  box-sizing: border-box;
  padding: 0 ${({ isImageExist }) => (isImageExist ? '32px' : 0)};
  margin-top: ${({ isImageSmall, isImageExist }) => (isImageSmall && isImageExist ? '48px' : 0)};

  grid-auto-flow: dense;
  grid-gap: 32px;

  grid-template-columns: ${({ isImageSmall, isImageExist }) => {
    if (!isImageExist) {
      return 'initial';
    }
    return isImageSmall ? '1fr 1fr' : '416px auto';
  }};

  ${({ isImageSmall, isImageExist }) => isImageSmall
    && isImageExist
    && screen.l(css`
      margin-top: 40px;
    `)}

  &:nth-child(2n) {
    grid-template-columns: ${({ isImageSmall }) => (isImageSmall ? 'auto 250px' : 'auto 416px')};

    ${screen.l(css`
      grid-template-columns: inherit;
    `)}

    & > :first-child {
      grid-column: 2;

      ${screen.l(css`
        grid-column: inherit;
      `)}
    }

    & > :last-child {
      grid-column: 1;

      ${screen.l(css`
        grid-column: inherit;
      `)}
    }
  }

  &:not(:last-child) {
    margin-bottom: 48px;

    ${({ isImageSmall }) => (isImageSmall
    ? screen.l(css`
            margin-bottom: 48px;
          `)
    : css`
            margin-bottom: 48px;
          `)}
  }

  ${screen.l(css`
    padding: 0 16px;
    margin-bottom: 48px;
    grid-gap: 16px;
    grid-template-columns: inherit;
  `)}
`;

const StyledItemImage = styled(Link)`
  text-decoration: none;

  img {
    display: block;
    width: 100%;
    max-width: 100%;
    height: ${({ isImageSmall }) => (isImageSmall ? 230 : 320)}px;
    object-fit: cover;
  }
`;

const StyledItemContent = styled.div``;

const buttonCssRules = css`
  margin-top: 32px;

  ${({ isImageSmall }) => isImageSmall
    && screen.l(css`
      display: none;
    `)}
`;

const articleContentCssRules = css`
  line-height: 21px !important;

  &:not(:last-child) {
    margin-bottom: 16px !important;
  }
`;

const InformationItems = ({
  list,
  isImageLinkDisabled = false,
  listCssRules,
  isImageSmall = false,
  buttonProps = {},
  isFactAndFigure = false,
}) => {
  const getImageUrl = (imageUrl) => {
    if (!imageUrl.indexOf('http')) {
      return imageUrl;
    }
    return require(`@assets/img${imageUrl}`);
  };

  const getActionButtonProps = (id) => {
    if (isImageLinkDisabled) {
      return {};
    }

    return {
      to: buttonProps.isCustomButtonLink ? `${id}/` : `/company-news/${id}/`,
    };
  };

  const itemsList = useMemo(
    () => list
      && list.map(({
        id, imageUrl, title, subTitle = '', sections,
      }) => {
        const {
          title: buttonTitle = 'Read more',
          color: buttonColor,
        } = buttonProps;
        const isImageExist = !!imageUrl?.childImageSharp?.gatsbyImageData;
        const fullImagePath = isImageExist && imageUrl?.childImageSharp?.gatsbyImageData;
        return (
          <StyledItemContainer
            isImageSmall={isImageSmall}
            isImageExist={isImageExist}
          >
            {isImageExist && (
              <StyledItemImage
                isImageSmall={isImageSmall}
                {...getActionButtonProps(id)}
              >
                <GatsbyImage
                  image={fullImagePath}
                  alt={title}
                  title={title}
                  loading="lazy"
                  objectFit="cover"
                  backgroundColor="#EEEEEE"
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    height: isImageSmall ? '230px' : '320px',
                    minHeight: '100%',
                  }} />
              </StyledItemImage>
            )}
            <StyledItemContent>
              <InformationTitle
                title={title}
                subTitle={subTitle}
                isImageSmall={isImageSmall}
                isImageExist={isImageExist}
                isCustomButtonLink={buttonProps.isCustomButtonLink}
                to={`${id}/`}
                isFactAndFigure={isFactAndFigure}
              />
              <ArticleContent
                content={sections}
                cssRules={articleContentCssRules}
                buttonType="black"
              />
              {!isEmpty(buttonProps) && (
                <Button
                  title={buttonTitle}
                  color={buttonColor}
                  cssRules={buttonCssRules}
                  {...getActionButtonProps(id)}
                />
              )}
            </StyledItemContent>
          </StyledItemContainer>
        );
      }),
    [isImageSmall, buttonProps, list, getActionButtonProps],
  );

  return (
    <StyledItemsContainer
      cssRules={listCssRules}
      isImageSmall={isImageSmall}
      isImageExist={!!list && !!list[0].imageUrl}
    >
      {list && itemsList}
    </StyledItemsContainer>
  );
};

export default InformationItems;
